import clsx from 'clsx';
import { isValidElement, useEffect, type ReactElement } from 'react';
import ErrorImg from '../../../assets/error-banner.svg?react';
import InfoImg from '../../../assets/info-banner.svg?react';
import SuccessImg from '../../../assets/success-banner.svg?react';
import WarningImg from '../../../assets/warning-banner.svg?react';
import './banner.less';
import { ERROR_BANNER_AUTO_CLOSE_MS } from '../../../constants';

export type NOTIF_BANNER_TYPE = 'error' | 'warning' | 'info' | 'success';

export interface BannerProps {
  status: NOTIF_BANNER_TYPE;
  message?: string;
  subMessage?: string;
  customIcon?: string | ReactElement | (() => JSX.Element);
  className?: string;
  /**
   * Called when the banner is dismissed (close button or auto-close timer).
   * When omitted, the banner is non-dismissible.
   */
  clearBanner?: () => void;
}

const bannerImages: Record<NOTIF_BANNER_TYPE, ReactElement> = {
  error: <ErrorImg />,
  warning: <WarningImg />,
  info: <InfoImg />,
  success: <SuccessImg />,
};

export const Banner = ({
  status,
  message,
  subMessage,
  customIcon,
  className = '',
  clearBanner,
}: BannerProps) => {
  const dismissible = !!clearBanner;

  useEffect(() => {
    if (!dismissible) {
      return;
    }
    const timer = setTimeout(() => {
      clearBanner();
    }, ERROR_BANNER_AUTO_CLOSE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [dismissible, clearBanner]);

  const renderIcon = () => {
    if (typeof customIcon === 'string') {
      return <img src={customIcon} alt={status} />;
    }
    if (isValidElement(customIcon)) {
      return customIcon;
    }
    if (typeof customIcon === 'function') {
      const CustomIconComponent = customIcon;
      return <CustomIconComponent />;
    }
    return bannerImages[status];
  };

  return (
    <div role="alert" className={clsx('banner-root', status, className)}>
      {renderIcon()}
      <div className="banner-content">
        <div className="banner-title">{message}</div>
        {!!subMessage && <div className="banner-submessage">{subMessage}</div>}
      </div>
      {dismissible && (
        <button
          type="button"
          className="banner-close"
          aria-label="Close notification"
          onClick={clearBanner}
        >
          ×
        </button>
      )}
    </div>
  );
};
